/**
 * Which device a desktop preview draws the app in (frame mode only; a real
 * phone or tablet is device mode, deviceMode.ts): an iPhone (the default),
 * an iPad in landscape, a Mac window, or no device at all, the app filling
 * the window under the toolbar. styles.css and page.css draw each from the
 * document's `data-sb-frame`; the layout engine gets the device's safe area,
 * and its size from the screen's box, so an iPad (or a wide window) lays
 * out in the regular size class, sidebars and all. The Mac is the macOS
 * platform (docs/notes/macos.md): the renderer draws its idiom and tells the
 * app; the others are iOS.
 *
 * `?device=iphone|ipad|mac|none` picks one (and is what the picker writes,
 * so a link shares it); otherwise the device an app built for macOS asks
 * for (`preferred`), else the last one picked on this site, from
 * localStorage. The toolbar's close button (an x in a circle, at its end)
 * hides the toolbar and writes `?toolbar=0`, so a link shows the app alone;
 * without that parameter the toolbar is back. The pages' inline scripts apply the same rule before the
 * first paint (app/index.html's, and the native apps' web/index.html), so
 * an iPad never flashes an iPhone first.
 */

import type { EdgeInsets } from './layout';
import type { Platform } from './protocol';

export type DeviceFrame = 'iphone' | 'ipad' | 'mac' | 'none';

export const DEVICE_FRAMES: readonly { id: DeviceFrame; label: string }[] = [
  { id: 'iphone', label: 'iPhone' },
  { id: 'ipad', label: 'iPad' },
  { id: 'mac', label: 'Mac' },
  { id: 'none', label: 'None' },
];

/**
 * The safe area each device gives the app: an iPhone 15 Pro's status bar and
 * home indicator, an iPad's (24 and 20 points, as iPadOS draws them in
 * landscape), a Mac window's 32-point title bar (macOS 26's compact one) (its traffic lights drawn
 * by the page over the app, as the iPhone's status bar is), and none
 * without a device.
 */
export const FRAME_SAFE_AREAS: Readonly<Record<DeviceFrame, EdgeInsets>> = {
  iphone: { top: 59, leading: 0, bottom: 34, trailing: 0 },
  ipad: { top: 24, leading: 0, bottom: 20, trailing: 0 },
  mac: { top: 32, leading: 0, bottom: 0, trailing: 0 },
  none: { top: 0, leading: 0, bottom: 0, trailing: 0 },
};

/** The platform each device shows the app as: the Mac is macOS, the rest iOS. */
export function platformOf(frame: DeviceFrame): Platform {
  return frame === 'mac' ? 'macos' : 'ios';
}

/** The device an app built for a platform is shown in when nothing picks one. */
export function frameFor(platform: Platform | undefined): DeviceFrame {
  return platform === 'macos' ? 'mac' : 'iphone';
}

/** Where the last device picked is kept. */
export const DEVICE_FRAME_KEY = 'sb-device-frame';

const isFrame = (value: unknown): value is DeviceFrame => value === 'iphone' || value === 'ipad' || value === 'mac' || value === 'none';

/** `?device=`, else `preferred` (the device the app was built for), else the stored choice, else the iPhone. */
export function decideDeviceFrame(search: string = location.search, storage: Pick<Storage, 'getItem'> | null = safeStorage(), preferred?: DeviceFrame): DeviceFrame {
  const asked = new URLSearchParams(search).get('device');
  if (isFrame(asked)) return asked;
  if (preferred !== undefined && preferred !== 'iphone') return preferred;
  let stored: string | null = null;
  try {
    stored = storage?.getItem(DEVICE_FRAME_KEY) ?? null;
  } catch {
    // Storage blocked: the default.
  }
  return isFrame(stored) ? stored : 'iphone';
}

/**
 * Marks the document with the device (`data-sb-frame`) and gives the status
 * bar the date an iPad shows beside the time.
 */
export function applyDeviceFrame(frame: DeviceFrame, doc: Document = document): void {
  doc.documentElement.dataset['sbFrame'] = frame;
  const time = doc.querySelector('.status-time');
  if (time && !time.querySelector('.status-date')) {
    const date = doc.createElement('span');
    date.className = 'status-date';
    date.textContent = 'Tue Jan 9';
    time.append(date);
  }
}

/** What the picker drives: the renderer (or a mounted React root, which tells its tree too). */
export interface DeviceFrameTarget {
  setSafeArea(insets: EdgeInsets | null): void;
  viewportChanged(): void;
  /** The platform the device shows (`platformOf`); the renderer draws its idiom. */
  setEnvironment(environment: { platform: Platform }): void;
}

export interface DeviceFramePickerOptions {
  /** The page's toolbar, which gets the picker. */
  toolbar?: HTMLElement | null;
  /** The toolbar item the picker goes before; the theme toggle by default, else the end. */
  before?: Element | null;
  search?: string;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** Called after each change of device, the new one applied. */
  onChange?: (frame: DeviceFrame) => void;
  /** The close button at the toolbar's end (`installToolbarClose`); on unless false. */
  closeButton?: boolean;
  /** The device the app asks for (`frameFor` its platform), over the remembered one. */
  preferred?: DeviceFrame;
}

/**
 * A desktop preview's device: applies the chosen one, lays the app out for
 * it, and adds a Device picker to the toolbar that switches it in place (the
 * app keeps its state) and remembers it. Does nothing in device mode, where
 * the page is the device. Returns the device in use.
 */
export function installDeviceFrame(target: DeviceFrameTarget, options: DeviceFramePickerOptions = {}): DeviceFrame {
  const doc = document;
  if (doc.documentElement.dataset['sbMode'] === 'device') return 'iphone';
  const storage = options.storage === undefined ? safeStorage() : options.storage;
  let frame = decideDeviceFrame(options.search ?? location.search, storage, options.preferred);
  const show = (next: DeviceFrame): void => {
    frame = next;
    applyDeviceFrame(next, doc);
    target.setSafeArea(FRAME_SAFE_AREAS[next]);
    target.setEnvironment({ platform: platformOf(next) });
    target.viewportChanged();
  };
  show(frame);

  const toolbar = options.toolbar === undefined ? doc.querySelector<HTMLElement>('.toolbar') : options.toolbar;
  if (toolbar && options.closeButton !== false) installToolbarClose(toolbar, target);
  if (!toolbar || toolbar.querySelector('#device-frame')) return frame;
  const label = doc.createElement('label');
  label.className = 'toolbar-label';
  label.htmlFor = 'device-frame';
  label.textContent = 'Device';
  const select = doc.createElement('select');
  select.id = 'device-frame';
  select.className = 'toolbar-select';
  for (const { id, label: text } of DEVICE_FRAMES) select.append(new Option(text, id, false, id === frame));
  select.addEventListener('change', () => {
    if (!isFrame(select.value)) return;
    show(select.value);
    try {
      storage?.setItem(DEVICE_FRAME_KEY, select.value);
    } catch {
      // Storage full or blocked: the choice lasts the page.
    }
    const url = new URL(location.href);
    if (select.value === 'iphone') url.searchParams.delete('device');
    else url.searchParams.set('device', select.value);
    history.replaceState(history.state, '', url);
    options.onChange?.(frame);
  });
  const before = options.before === undefined ? toolbar.querySelector('#theme-toggle') : options.before;
  if (before && before.parentElement === toolbar) {
    toolbar.insertBefore(label, before);
    toolbar.insertBefore(select, before);
  } else {
    toolbar.append(label, select);
  }
  return frame;
}

/** Whether the URL asks for the page without its toolbar (`?toolbar=0`). */
export function toolbarHidden(search: string = location.search): boolean {
  return new URLSearchParams(search).get('toolbar') === '0';
}

/**
 * An x in a circle at the end of the toolbar that hides it, for the app
 * alone in the page (a screenshot, a demo): the document gets
 * `data-sb-toolbar="hidden"` (page.css), the URL `?toolbar=0` so a reload
 * or a shared link keeps it hidden, and the renderer re-reads the screen's
 * size, which grows without a device. The pages' inline scripts hide the
 * toolbar before the first paint when the URL asks.
 */
export function installToolbarClose(toolbar: HTMLElement, target?: Pick<DeviceFrameTarget, 'viewportChanged'>): HTMLButtonElement {
  const doc = toolbar.ownerDocument;
  const existing = toolbar.querySelector<HTMLButtonElement>('.toolbar-close');
  if (existing) return existing;
  if (toolbarHidden()) doc.documentElement.dataset['sbToolbar'] = 'hidden';
  const button = doc.createElement('button');
  button.type = 'button';
  button.className = 'toolbar-close';
  button.setAttribute('aria-label', 'Hide the toolbar');
  button.title = 'Hide the toolbar (remove ?toolbar=0 from the address to show it again)';
  button.innerHTML =
    '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><circle cx="10" cy="10" r="10" /><path d="M6.5 6.5l7 7M13.5 6.5l-7 7" /></svg>';
  button.addEventListener('click', () => {
    doc.documentElement.dataset['sbToolbar'] = 'hidden';
    const url = new URL(location.href);
    url.searchParams.set('toolbar', '0');
    history.replaceState(history.state, '', url);
    target?.viewportChanged();
  });
  toolbar.append(button);
  return button;
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
